import { motion } from "framer-motion";
import { SEO } from "@/components/shared";
import { fadeInUp, staggerContainer } from "@/constants";

export default function TermsPage() {
  return (
    <div className="pt-24">
      <SEO
        title="Terms & Conditions"
        description="Terms and Conditions governing the use of Qbic Catalyst Partners website and services."
        keywords="terms and conditions, Qbic Catalyst, consulting terms, service agreement"
        path="/terms"
      />
      <section className="section bg-[#0A1628]">
        <div className="container-custom">
          <motion.div initial="initial" animate="animate" variants={staggerContainer}>
            <motion.p variants={fadeInUp} className="text-[#C9A962] text-sm uppercase tracking-[0.2em] mb-4">Legal</motion.p>
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl font-bold mb-6">Terms & Conditions</motion.h1>
            <motion.p variants={fadeInUp} className="text-[#A0ABC0] mb-2">Effective Date: 01 January 2025</motion.p>
            <motion.p variants={fadeInUp} className="text-[#A0ABC0] mb-12">Last Updated: February 2026</motion.p>
          </motion.div>
        </div>
      </section>

      <section className="section bg-[#1A2A40]">
        <div className="container-custom max-w-4xl">
          <div className="prose-custom space-y-10">

            <div>
              <h2>1. Introduction</h2>
              <p>Welcome to the website of Qbic Catalyst Partners Pvt. Ltd. ("QCP", "we", "us", or "our"). By accessing or using our website at www.qbic-catalyst.com (the "Site") and any services offered through it, you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree with any part of these Terms, please do not use our Site or services.</p>
              <p>Qbic Catalyst Partners is a Govt. of India recognized Ed-Tech company and ISO/IEC 27001:2022 certified firm specializing in consulting and training services.</p>
            </div>

            <div>
              <h2>2. Services</h2>
              <p>QCP provides consulting, training, and skill development services including but not limited to:</p>
              <ul>
                <li>Go-To-Market Consulting for enterprises</li>
                <li>Digital Transformation Consulting for MSMEs</li>
                <li>AI Catalyst&#8482; Certification Program</li>
                <li>Intel&#174; Unnati Partnership Programs</li>
                <li>Intel&#174; AI for Future Workforce Programs</li>
                <li>Sales Transformation Programs (Explore Beyond Your Boundaries&#8482;)</li>
              </ul>
              <p>The scope, duration, and terms of each service engagement shall be defined in separate agreements, proposals, or statements of work between QCP and the client.</p>
            </div>

            <div>
              <h2>3. Use of the Website</h2>
              <p>You agree to use the Site only for lawful purposes and in accordance with these Terms. You shall not:</p>
              <ul>
                <li>Use the Site in any way that violates any applicable law or regulation.</li>
                <li>Attempt to gain unauthorized access to any part of the Site, its servers, or any connected systems.</li>
                <li>Use any automated system, including bots, scrapers, or crawlers, to access or interact with the Site without our prior written consent.</li>
                <li>Transmit any viruses, malware, or other harmful code through the Site.</li>
                <li>Impersonate any person or entity or misrepresent your affiliation with any person or entity.</li>
              </ul>
            </div>

            <div>
              <h2>4. Intellectual Property</h2>
              <p>All content on the Site, including but not limited to text, graphics, logos, images, audio, video, software, and the compilation thereof, is the property of Qbic Catalyst Partners Pvt. Ltd. or its licensors and is protected by Indian and international intellectual property laws.</p>
              <p>"AI Catalyst&#8482;", "Explore Beyond Your Boundaries&#8482;", and the QCP logo are trademarks of Qbic Catalyst Partners Pvt. Ltd. Unauthorized use of any QCP trademarks is strictly prohibited.</p>
              <p>Intel&#174; and Intel&#174; Unnati are trademarks of Intel Corporation. All third-party trademarks referenced on the Site belong to their respective owners.</p>
            </div>

            <div>
              <h2>5. Program Enrollment & Fees</h2>
              <ul>
                <li><strong>Enrollment:</strong> Participation in QCP programs is subject to eligibility criteria and availability. QCP reserves the right to accept or decline enrollment at its discretion.</li>
                <li><strong>Fees:</strong> Program fees are as communicated in the relevant proposal or enrollment form. All fees are exclusive of applicable taxes (including GST) unless stated otherwise.</li>
                <li><strong>Refunds:</strong> All fees paid are non-refundable unless explicitly stated in the program-specific agreement.</li>
                <li><strong>Modifications:</strong> QCP reserves the right to modify program structure, content, schedule, and fees. Participants will be notified of any significant changes in advance.</li>
              </ul>
            </div>

            <div>
              <h2>6. Certification & Placement</h2>
              <p>Certification is awarded upon meeting the criteria specified for each program, including attendance, project completion, and examination requirements. QCP certifications are issued by QCP Academy and represent completion of the specified program.</p>
              <p>Internship and placement assistance is provided as a value-added benefit. QCP facilitates introductions and profile sharing with partner organizations but does not guarantee any internship, placement, or employment outcome. All hiring decisions are at the sole discretion of the respective hiring organizations.</p>
            </div>

            <div>
              <h2>7. Limitation of Liability</h2>
              <p>To the maximum extent permitted by applicable law:</p>
              <ul>
                <li>QCP's total liability for any claim arising from or related to these Terms or the use of our services shall not exceed the amount actually paid by you for the specific service giving rise to the claim.</li>
                <li>QCP shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or business opportunities.</li>
                <li>Services are provided on an "as is" basis. QCP disclaims all warranties, express, implied, or statutory, including warranties of merchantability and fitness for a particular purpose.</li>
              </ul>
            </div>

            <div>
              <h2>8. Indemnification</h2>
              <p>You agree to indemnify and hold harmless QCP, its directors, officers, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from your use of the Site or breach of these Terms.</p>
            </div>

            <div>
              <h2>9. Third-Party Links</h2>
              <p>The Site may contain links to third-party websites or services. QCP is not responsible for the content, privacy policies, or practices of any third-party sites. Accessing third-party links is at your own risk.</p>
            </div>

            <div>
              <h2>10. Amendments</h2>
              <p>QCP reserves the right to modify these Terms at any time. For material changes, we will provide at least 30 days' notice by updating the "Last Updated" date on this page. Continued use of the Site after revisions constitutes acceptance of the revised Terms.</p>
            </div>

            <div>
              <h2>11. Severability</h2>
              <p>If any provision of these Terms is held to be unenforceable or invalid, it will be modified to accomplish its objectives to the greatest extent possible under applicable law, and the remaining provisions shall continue in full force and effect.</p>
            </div>

            <div>
              <h2>12. Governing Law & Jurisdiction</h2>
              <p>These Terms are governed by and construed in accordance with the laws of India. Any disputes arising under or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of Bangalore, India.</p>
            </div>

            <div>
              <h2>13. Contact Us</h2>
              <p>If you have any questions about these Terms, please contact us:</p>
              <p><strong>Qbic Catalyst Partners Pvt. Ltd.</strong><br />
              Email: <a href="mailto:info@qbiccatalyst.com" className="text-[#C9A962] hover:underline">info@qbiccatalyst.com</a><br />
              Website: <a href="https://www.qbic-catalyst.com" className="text-[#C9A962] hover:underline">www.qbic-catalyst.com</a></p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
